import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import { topupPackagesService } from "../../services/topup-packages.service";
import { TopUpPackage, CreateTopUpPackageDto } from "../../interfaces/topup-package.interface";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { useConfirm } from '../../hooks/useConfirm';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateTopUpPackageDto = {
  productId: "",
  points: 0,
  bonusPoints: 0,
  price: 0,
  currency: "USD",
  bestValue: false,
  sortOrder: 0,
  isActive: true,
};

function getErrorMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as any).response?.data;
    if (r?.message) return Array.isArray(r.message) ? r.message.join(", ") : r.message;
  }
  return fallback;
}

// ─── Form ─────────────────────────────────────────────────────────────────────

interface PackageFormProps {
  form: CreateTopUpPackageDto;
  setF: (patch: Partial<CreateTopUpPackageDto>) => void;
  saving: boolean;
  error: string;
  onSubmit: () => void;
  onCancel: () => void;
  isEdit: boolean;
}

const PackageForm = ({ form, setF, saving, error, onSubmit, onCancel, isEdit }: PackageFormProps) => {
  const { t } = useTranslation("common");

  const inputClass =
    "w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Product ID */}
      <div>
        <label className={labelClass}>{t("product_id")} *</label>
        <input
          className={inputClass}
          placeholder="com.marine2go.app.points_1"
          value={form.productId}
          onChange={(e) => setF({ productId: e.target.value })}
          disabled={isEdit}
        />
        {isEdit && (
          <p className="mt-1 text-xs text-gray-400">{t("key_immutable_hint")}</p>
        )}
      </div>

      {/* Points + Bonus */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>{t("points")} *</label>
          <input
            type="number"
            min={1}
            className={inputClass}
            value={form.points || ""}
            onChange={(e) => setF({ points: parseInt(e.target.value, 10) || 0 })}
          />
        </div>
        <div>
          <label className={labelClass}>{t("bonus_points")}</label>
          <input
            type="number"
            min={0}
            className={inputClass}
            value={form.bonusPoints || ""}
            onChange={(e) => setF({ bonusPoints: parseInt(e.target.value, 10) || 0 })}
          />
          {form.bonusPoints > 0 && (
            <p className="mt-1 text-xs text-green-600 dark:text-green-400">
              {t("total_receive", { total: (form.points || 0) + form.bonusPoints })}
            </p>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>{t("price")} *</label>
          <input
            type="number"
            min={0.01}
            step={0.01}
            className={inputClass}
            value={form.price || ""}
            onChange={(e) => setF({ price: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div>
          <label className={labelClass}>{t("currency")}</label>
          <select
            className={inputClass}
            value={form.currency}
            onChange={(e) => setF({ currency: e.target.value })}
          >
            <option value="USD">USD</option>
            <option value="HKD">HKD</option>
            <option value="SGD">SGD</option>
            <option value="AUD">AUD</option>
          </select>
        </div>
      </div>

      {/* Sort Order + Flags */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>{t("sort_order")}</label>
          <input
            type="number"
            min={0}
            className={inputClass}
            value={form.sortOrder}
            onChange={(e) => setF({ sortOrder: parseInt(e.target.value, 10) || 0 })}
          />
        </div>
        <div className="flex flex-col justify-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="rounded"
              checked={form.bestValue}
              onChange={(e) => setF({ bestValue: e.target.checked })}
            />
            {t("best_value")}
          </label>
        </div>
        <div className="flex flex-col justify-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="rounded"
              checked={form.isActive}
              onChange={(e) => setF({ isActive: e.target.checked })}
            />
            {t("active")}
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button variant="primary" size="sm" onClick={onSubmit} disabled={saving}>
          {saving ? t("loading") : isEdit ? t("save_changes") : t("create")}
        </Button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const TopUpPackagesPage = () => {
  const { t } = useTranslation("common");
  const { confirm, ConfirmDialog } = useConfirm();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.SYSTEM_SETTINGS_UPDATE);

  const [packages, setPackages] = useState<TopUpPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TopUpPackage | null>(null);
  const [form, setForm] = useState<CreateTopUpPackageDto>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<TopUpPackage | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await topupPackagesService.list();
      setPackages(data);
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_load")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (pkg: TopUpPackage) => {
    setEditTarget(pkg);
    setForm({
      productId:   pkg.productId,
      points:      pkg.points,
      bonusPoints: pkg.bonusPoints,
      price:       Number(pkg.price),
      currency:    pkg.currency,
      bestValue:   pkg.bestValue,
      sortOrder:   pkg.sortOrder,
      isActive:    pkg.isActive,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const setF = (patch: Partial<CreateTopUpPackageDto>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async () => {
    if (!form.productId.trim()) { setFormError(t("error_product_id_required")); return; }
    if (!form.points || form.points < 1) { setFormError(t("error_points_min")); return; }
    if (!form.price || form.price <= 0) { setFormError(t("error_price_required")); return; }

    if (!await confirm(t('confirm_save_topup_package'))) return;
    setSaving(true);
    setFormError("");
    try {
      if (editTarget) {
        const updated = await topupPackagesService.update(editTarget.id, form);
        setPackages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      } else {
        const created = await topupPackagesService.create(form);
        setPackages((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
      }
      setDialogOpen(false);
    } catch (e) {
      setFormError(getErrorMsg(e, t("failed_to_save")));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await topupPackagesService.remove(deleteTarget.id);
      setPackages((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_delete")));
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (pkg: TopUpPackage) => {
    if (!await confirm(t('confirm_toggle_topup_active'))) return;
    try {
      const updated = await topupPackagesService.update(pkg.id, { isActive: !pkg.isActive });
      setPackages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_save")));
    }
  };

  return (
    <Layout title={t("topup_packages")}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("topup_packages_desc")}
          </p>
          {canEdit && (
            <Button variant="primary" size="sm" onClick={openCreate}>
              + {t("create")}
            </Button>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">{t("loading")}</div>
        ) : packages.length === 0 ? (
          <div className="text-center py-12 text-gray-400">{t("noData")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((pkg) => {
              const totalPts = pkg.points + pkg.bonusPoints;
              return (
                <Card
                  key={pkg.id}
                  className={`p-4 space-y-3 relative ${!pkg.isActive ? "opacity-50" : ""}`}
                >
                  {/* Badges */}
                  <div className="flex items-center gap-2 flex-wrap min-h-[24px]">
                    {pkg.bestValue && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
                        ★ {t("best_value")}
                      </span>
                    )}
                    {!pkg.isActive && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 font-medium">
                        {t("inactive")}
                      </span>
                    )}
                  </div>

                  {/* Points */}
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {totalPts.toLocaleString()} pts
                    </div>
                    {pkg.bonusPoints > 0 && (
                      <div className="text-xs text-green-600 dark:text-green-400">
                        {pkg.points.toLocaleString()} + {pkg.bonusPoints.toLocaleString()} {t("bonus")}
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    {pkg.currency} {Number(pkg.price).toFixed(2)}
                  </div>

                  {/* Meta */}
                  <div className="text-xs text-gray-400 space-y-0.5">
                    <div>{pkg.productId}</div>
                    <div>{t("sort_order")}: {pkg.sortOrder}</div>
                  </div>

                  {/* Actions */}
                  {canEdit && (
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <Button variant="outline" size="sm" onClick={() => openEdit(pkg)}>
                        {t("edit")}
                      </Button>
                      <Button
                        variant={pkg.isActive ? "secondary" : "primary"}
                        size="sm"
                        onClick={() => toggleActive(pkg)}
                      >
                        {pkg.isActive ? t("deactivate") : t("activate")}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="ml-auto"
                        onClick={() => setDeleteTarget(pkg)}
                      >
                        {t("delete")}
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        title={editTarget ? t("edit_topup_package") : t("create_topup_package")}
        onClose={() => setDialogOpen(false)}
      >
        <PackageForm
          form={form}
          setF={setF}
          saving={saving}
          error={formError}
          onSubmit={handleSubmit}
          onCancel={() => setDialogOpen(false)}
          isEdit={!!editTarget}
        />
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteTarget}
        title={t("delete_topup_package")}
        onClose={() => setDeleteTarget(null)}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t("delete_topup_package_confirm")} <strong>{deleteTarget?.productId}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("cancel")}
            </Button>
            <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? t("loading") : t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
      {ConfirmDialog}
    </Layout>
  );
};

export default TopUpPackagesPage;
